import { AccountCenterSection } from '../utilities/accountCenterSection.component';
import { ModalService } from '../../../services/modal/modal.service';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { ModalType } from '../../../../js/interfaces/CoverpathTypes';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { BasePolicyStatus } from '../policy-status/base-policy-status';
import type { CoverpathPolicy } from '../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import type { MMDirectPolicy } from '../../../../../coverpath-common/classes/mmdirect_subclasses/MMDirectPolicy';
import { IsLoadingType } from '../../../../../coverpath-common/angular/interfaces/account-center/IsLoadingDataInterface';
import { PolicyData } from '../../../interfaces/PolicyData';
import { ToastService } from '../../overlays/toaster/toast-service.service';
import { CasApplication } from '../../../../classes/coverpath_subclasses/CasApplication';

export class BasePolicyBannerComponent extends AccountCenterSection {
  action: string;
  policy: CoverpathPolicy | MMDirectPolicy;
  isLiteTouch: boolean;
  modalSvc: ModalService;
  toastSvc: ToastService;
  buttonText: string = null;
  clickAction: () => void;

  loadingEsign: boolean;
  isLoading: boolean;
  hasPaymentFailed: boolean;
  hasPendingAmendment: boolean;
  insurabilitySignatureIsRequired: boolean = false;
  revisedBasicIllustrationSignatureRequired: boolean = false;
  customerSignedApp: boolean;
  policyStatus: BasePolicyStatus;
  bannerTitles: string[];
  bannerSubTitles: string[];
  isAccelerated: boolean = false;
  isFrozen: boolean = false;
  insuredEsignRequired: boolean = false;
  accountCenterService;
  policyData: PolicyData;
  needsAuthorizationWithinWindow: boolean;
  needsAuthorizationPastdue: boolean;

  constructor(
    protected amorphicService: AmorphicService,
    accountCenterService: AccountCenterService,
    modalService: ModalService,
    toastService: ToastService
  ) {
    super(amorphicService, accountCenterService);
    this.modalSvc = modalService;
    this.toastSvc = toastService;
    this.accountCenterService = accountCenterService;
  }

  async setAllProperties(): Promise<void> {
    this.policy = this.amorphicService.controller.customer.application.primaryPolicy;
    const policyNumber = this.policy.policyNumber;
    let secondaryPolicyInfo;
    const application: CasApplication = this.policy.application as CasApplication;
    if (application.isJointAppWithPendingAmendment()) {
      secondaryPolicyInfo = this.accountCenterService.getClientAccountCenterViewDataFromPolicy(
        application.secondaryPolicyWithPendingAmendment.__id__ as string
      );
    }
    const isAnnuity = this.policy.isAnnuity();
    //Adding this to minimize impact to Annuities and MMDirect
    if (!isAnnuity) {
      this.policyData = this.accountCenterService.getPolicyDataFromPolicy(this.policy.__id__);
      this.policyStatus.primaryPolicyData = this.policyData;
      this.customerSignedApp = this.policyData.getCACCustomerSignedApp();
      this.isFrozen = this.policyData.getCACIsFrozen();
      this.hasPendingAmendment = this.policyData.getCACHasPendingAmendment();
      this.insurabilitySignatureIsRequired = this.policyData.getCACInsurabilitySignatureIsRequired();
      this.isAccelerated = this.policyData.getCACIsAccelerated();
      this.isLiteTouch = this.policyData.getCACIsLiteTouch();
      this.policyStatus.setAllProperties();
    } else {
      this.customerSignedApp = this.policy.applicationSignedDate !== null;
      this.isFrozen = this.policy.application.isFrozen;
      this.hasPendingAmendment = this.policy.hasPendingAmendment();
      this.insurabilitySignatureIsRequired = CoverpathUtils.isInsurabilityStatementSignatureRequired(this.policy);
      this.isAccelerated = this.policy.isAccelerated();
      this.isLiteTouch = this.policy.isLiteTouch;
    }

    this.revisedBasicIllustrationSignatureRequired =
      await this.amorphicService.controller.accountCenterController.customerNeedsToSignRevisedBasicIllustration(
        policyNumber
      );

    //`workflowAction` needs to updated explicitly because the workflowState and workflowSubState on the policy could be stale
    //`subscribeToAccountCenterService` has the latest workflowAction
    this.policyStatus.workflowAction = this.action;
    this.policyStatus.policy = this.policy;
    // have to do this if check until all policy banners pass in account center service
    const { title, subtitle } = await this.policyStatus.getPolicyStatusText();
    this.bannerTitles = this.accountCenterService.bannerTitles = title;
    this.bannerSubTitles = this.accountCenterService.bannerSubTitles = subtitle;
    this.hasPaymentFailed =
      (secondaryPolicyInfo && secondaryPolicyInfo.getCACHasPaymentFailed()) ||
      this.policyData.getCACPaymentData().getCACHasPaymentFailed();
    this.needsAuthorizationWithinWindow = this.policyData.getCACPaymentData().getCACNeedsAuthorizationWithinWindow();
    this.needsAuthorizationPastdue = this.policyData.getCACPaymentData().getCACNextPaymentDisplayDDBPast();
  }

  async processWorkflowAction(workflowAction: string): Promise<void> {
    this.action = workflowAction;
    await this.setAllProperties();
    this.setupButtonActions();
  }

  setupButtonActions() {
    const eSignBtnText = 'Continue to eSign';
    const acceptOfferBtnText = 'Accept Offer';
    const clientSignature = 'Continue to signature';
    const tlicOfferBtnText = 'Continue';
    const approve1035BtnText = 'Approve 1035 exchange funds transfer';
    const conversionBtnText = 'Continue to payment';
    const authorizePaymentBtnText = 'Make a payment';
    const isAccelerated = this.policyData.getCACIsAccelerated();
    const clientEsignNeeded =
      this.policyData.getCACInsurabilitySignatureIsRequired() || this.revisedBasicIllustrationSignatureRequired;
    const subState = this.policy.futureWorkflowSubState || this.policy.workflowSubState;
    const hasPendingAmendment = this.policyData.getCACHasPendingAmendment();

    if (CoverpathUtils.isTlicOfferStage(this.action)) {
      this.buttonText = tlicOfferBtnText;
      // skip tlic offer page if we are already in the signature workflow stage,
      // gathering directly from the policy, because an alias will have changed
      // the action
      if (subState === 'Waiting for Signature') {
        this.clickAction = this.eSign;
      } else {
        this.clickAction = this.openAcceptOrDeclineTlicOfferModal;
      }
    } else if (CoverpathUtils.isAcceptOfferStage(this.action) && !this.insuredEsignRequired) {
      this.buttonText = acceptOfferBtnText;
      if (!this.policy.paymentInformationGathered) {
        this.buttonText = conversionBtnText;
        if ((hasPendingAmendment && !isAccelerated) || clientEsignNeeded) {
          this.clickAction = this.openPaymentAndClientEsignModal;
        } else {
          this.clickAction = this.openPaymentModal;
        }
      } else if ((hasPendingAmendment && !isAccelerated) || clientEsignNeeded) {
        this.buttonText = clientSignature;
        this.clickAction = this.openClientEsignModal;
      } else {
        this.clickAction = this.acceptOffer;
      }
    } else if (CoverpathUtils.isEsignOfferStage(this.action)) {
      this.buttonText = eSignBtnText;
      if (isAccelerated && !this.policy.paymentInformationGathered) {
        this.buttonText = conversionBtnText;
        this.clickAction = this.openExpressPaymentModal;
      } else if (isAccelerated && this.policy.paymentInformationGathered) {
        this.buttonText = clientSignature;
        this.clickAction = this.eSign;
      } else {
        this.clickAction = this.eSign;
      }
    } else if (CoverpathUtils.isFP1Stage(this.action)) {
      this.buttonText = approve1035BtnText;
      this.clickAction = this.acceptOfferPreview;
    } else if (this.policy.convertedFrom && subState === 'Conversion started') {
      this.buttonText = conversionBtnText;
      this.clickAction = this.openConversionPaymentModal;
    } else if (
      this.policy.isNotPartOfJointApp() &&
      (this.needsAuthorizationWithinWindow || this.needsAuthorizationPastdue || this.hasPaymentFailed)
    ) {
      this.buttonText = authorizePaymentBtnText;
      this.clickAction = this.openAuthorizePaymentModal;
    } else {
      this.buttonText = null;
    }
  }

  openAcceptOrDeclineTlicOfferModal(): void {
    this.modalSvc.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: 'confirm-tlic' },
    });
  }

  openExpressPaymentModal(): void {
    this.modalSvc.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: 'payment-express' },
    });
  }

  openPaymentModal(): void {
    this.modalSvc.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: 'payment' },
    });
  }

  openConversionPaymentModal(): void {
    this.modalSvc.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: 'conversion-payment' },
    });
  }

  openAuthorizePaymentModal(): void {
    this.modalSvc.activate({
      type: ModalType.AuthorizePayment,
      modalData: {
        policy: this.policy,
        paPolicyData: this.policyData,
      },
    });
  }

  openPaymentAndClientEsignModal(): void {
    this.modalSvc.activate({
      type: ModalType.paymentIfMissing,
      modalData: { instance: 'payment-then-client-esign' },
    });
  }

  openClientEsignModal(): void {
    this.modalSvc.activate({
      type: ModalType.clientEsign,
    });
  }

  onClickAction() {
    this.clickAction.call(this);
  }

  /**
   * Enforces only the first button click.
   * Ignores accidental subsequent clicks that can sometimes lead to double modal pop ups and weird page sizes
   * @param e
   */
  onSingleClickAction(e: MouseEvent) {
    if (e.detail === 1) {
      this.clickAction.call(this);
    }
  }

  async acceptOffer(): Promise<void> {
    this.accountCenterService.isLoadingSubject.next({
      isLoading: true,
      type: IsLoadingType.DURING,
      action: this.action,
    });
    await this.accountCenterService.acceptFromService();
  }

  async acceptOfferPreview(): Promise<void> {
    this.accountCenterService.isLoadingSubject.next({
      isLoading: true,
      type: IsLoadingType.DURING,
      action: this.action,
    });
    await this.accountCenterService.acceptOfferPreviewFromService();
    this.toastSvc.showToast('Thank you for approving your 1035 exchange funds transfer.');
  }

  async eSign(): Promise<void> {
    this.accountCenterService.isLoadingSubject.next({
      isLoading: true,
      type: IsLoadingType.DURING,
      action: this.action,
    });
    await this.accountCenterService.esignFromService();
  }

  resendEsignEmail(): void {
    this.accountCenterService.isLoadingSubject.next({ isLoading: true });
    setTimeout(() => {
      this.accountCenterService.isLoadingSubject.next({ isLoading: false });
    }, 3000); // TODO: HL-7682 Update with modal notification upon action success
    this.accountCenterService.resendFromService();
  }

  openScheduleMedicalExamModal(): void {
    this.modalSvc.activate({
      type: ModalType.scheduleMedicalExam,
    });
  }

  openModal(modalName: string): void {
    this.modalSvc.activate({
      type: ModalType[modalName],
    });
  }
}
